// src/config/comptesAlias.config.ts — SCE Issues
// Comptes secondaires qui représentent une personne interne.
//
// ─── Pourquoi ───
//
// Sartorius refuse toute connexion avec un compte pro depuis un appareil qui
// n'appartient pas à Sartorius. Pour qu'un salarié utilise l'appli depuis son
// téléphone personnel, l'IT invite son adresse perso comme compte invité B2B —
// voie validée explicitement par l'IT le 29/09/2026 pour Romuald Ruse.
//
// Sans cette table, ce compte serait un inconnu de plus : son UPN en `#EXT#`
// en fait un « fournisseur » (ses seules déclarations, ni qualification ni
// clôture), et tout ce qu'il écrit apparaîtrait sous un deuxième « Romuald ».
//
// ─── Ce que la table fait, et ce qu'elle ne peut pas faire ───
//
// Dans l'appli, le compte secondaire EST la personne : mêmes droits, mêmes
// préférences et suivis, présence signalée sous son nom, et ses déclarations
// relues sous son identité (Mes déclarations, statistiques, export).
//
// Côté SharePoint, rien ne fusionne deux identités : la colonne « Créé par »
// garde le compte invité, et les flows Power Automate, qui la lisent en
// direct, le voient aussi. D'où le nom d'affichage demandé à l'IT.
//
// ─── Pourquoi l'Object ID, et pas l'adresse ───
//
// Le bundle JavaScript est servi sans authentification et le dépôt garde tout
// en historique : une adresse personnelle n'a rien à y faire. L'Object ID
// Entra ne révèle rien et ne change jamais — contrairement à l'UPN d'un
// invité, qui suit son adresse. Le jeton MSAL (claim `oid`) et l'auteur lu
// par Graph (`createdBy.user.id`) portent ce même identifiant.
//
// ─── Sauf pour l'auteur relu dans SharePoint ───
//
// Constaté le 30/09 : pour un invité, Graph ne renvoie pas toujours l'Object
// ID dans `createdBy.user` — seulement l'adresse et le nom affiché. La session
// reconnaissait Romuald, mais « Déclaré par » affichait encore le compte
// invité. D'où un second repère : l'EMPREINTE SHA-256 de l'adresse (en
// minuscules), jamais l'adresse elle-même. Une adresse d'invité au format UPN
// (`x_gmail.com#EXT#@…`) est ramenée à `x@gmail.com` avant le calcul.
// Ce repère ne sert qu'à relire l'auteur : les DROITS se décident sur l'Object
// ID du jeton, et seulement sur lui (identiteDuCompte).
//
// ─── Comptes PARTAGÉS (07/10) ───
//
// Même voie pour un appareil commun : l'iPad de l'atelier Elec / Autom, où
// Thibaut Hacquin et Hugo Peultier travaillent sous UN compte invité (ajouté
// au groupe SharePoint par l'IT). Le compte ne dit pas qui le tient : l'appli
// le DEMANDE à l'ouverture (« Qui utilise la tablette ? »), le retient sur
// l'appareil, et on change dans le menu du compte (auth/comptePartage). Une
// fois choisie, la personne est traitée exactement comme Romuald plus haut.
//
// SharePoint ne voit qu'un auteur, le compte de la tablette. D'où la colonne
// texte SaisiPar (SCE_Problemes, SCE_Commentaires), écrite à la création par
// l'appareil partagé seulement : l'UPN de la personne choisie. Relu, l'auteur
// est cette personne ; sans SaisiPar (lignes d'avant la colonne), le nom de
// la tablette (`libelle`) — jamais Thibaut ou Hugo au hasard.

import { sha256Hex } from '@/utils/empreinte'
import { rolesCharges } from './registreRoles'

export interface PersonnePrincipale {
  upn: string
  email: string
  /** « Prénom Nom » ou « Nom, Prénom » : toujours relu par nomComplet(). */
  nom: string
}

export interface CompteAlias {
  /** Object ID Entra du compte secondaire (GUID, fourni par l'IT). */
  objectId: string
  /** SHA-256 (hex) de l'adresse du compte, en minuscules — pour relire l'auteur. */
  empreinteAdresse?: string
  /** La personne interne que ce compte représente. */
  principal: PersonnePrincipale
  /** Pourquoi ce compte existe et qui l'a validé — pour qui relira la table. */
  motif: string
}

/** Valeurs par défaut : le panneau d'administration (22/10, Comptes secondaires) les remplace dès qu'on y enregistre. */
export const COMPTES_ALIAS_DEFAUT: readonly CompteAlias[] = [
  {
    objectId: '1c5e443c-71bc-494d-9d56-b51abdc91dd5',
    empreinteAdresse: 'd0fd1b11de78d3d857a163853117561a15380860bc94326b7a95c38c791a9d01',
    principal: {
      upn: 'romuald.ruse@sartorius.com',
      email: 'romuald.ruse@sartorius.com',
      nom: 'Romuald Ruse',
    },
    motif: 'Téléphone personnel — compte invité validé par l’IT le 29/09/2026',
  },
]

/** Un appareil commun à plusieurs personnes, sous un seul compte. */
export interface ComptePartage {
  /** Object ID Entra du compte de l'appareil (GUID, fourni par l'IT). */
  objectId: string
  /** SHA-256 (hex) de l'adresse du compte, en minuscules — pour relire l'auteur. */
  empreinteAdresse?: string
  /** Nom de l'appareil, affiché comme auteur de ce qu'il a écrit. */
  libelle: string
  /** Les personnes qui s'en servent, dans l'ordre de la question. */
  personnes: readonly PersonnePrincipale[]
  motif: string
}

/** Valeurs par défaut : le panneau d'administration (22/10, Appareils partagés) les remplace dès qu'on y enregistre. */
export const COMPTES_PARTAGES_DEFAUT: readonly ComptePartage[] = [
  {
    objectId: 'c1f91afe-051f-42c8-abbe-2a5359b0d10f',
    empreinteAdresse: '29db6286ef602d6573681f5801f3392196013ac40379636abfb042c52276a3de',
    libelle: 'Tablette Elec / Autom',
    personnes: [
      { upn: 'thibaut.hacquin@sartorius.com', email: 'thibaut.hacquin@sartorius.com', nom: 'Thibaut Hacquin' },
      { upn: 'hugo.peultier@sartorius.com', email: 'hugo.peultier@sartorius.com', nom: 'Hugo Peultier' },
    ],
    motif: 'iPad de l’atelier Elec / Autom, protégé par un code — compte invité ajouté par l’IT le 07/10/2026',
  },
]

const enPrincipal = (p: { nom: string; upn: string }): PersonnePrincipale => ({ upn: p.upn, email: p.upn, nom: p.nom })

/** Les comptes secondaires en vigueur : ceux du panneau d'administration, sinon ceux d'ici. */
export function comptesAlias(): readonly CompteAlias[] {
  const r = rolesCharges()
  if (!r) return COMPTES_ALIAS_DEFAUT
  return r.secondaires.map((s) => ({
    objectId: s.objectId,
    ...(s.empreinteAdresse ? { empreinteAdresse: s.empreinteAdresse } : {}),
    principal: enPrincipal(s.personne),
    motif: s.appareil,
  }))
}

/** Les appareils partagés en vigueur : ceux du panneau d'administration, sinon ceux d'ici. */
export function comptesPartages(): readonly ComptePartage[] {
  const r = rolesCharges()
  if (!r) return COMPTES_PARTAGES_DEFAUT
  return r.appareils.map((a) => ({
    objectId: a.objectId,
    ...(a.empreinteAdresse ? { empreinteAdresse: a.empreinteAdresse } : {}),
    libelle: a.libelle,
    personnes: a.personnes.map(enPrincipal),
    motif: a.libelle,
  }))
}

function normaliser(id: string | undefined): string {
  return (id ?? '').trim().toLowerCase()
}

/**
 * La personne représentée par ce compte, ou null s'il ne représente personne.
 *
 * ⚠️ Un identifiant vide ne correspond JAMAIS : un jeton privé de sa claim
 *    `oid` ne doit pas hériter des droits d'un coordinateur par accident.
 */
export function principalDuCompte(
  objectId: string | undefined,
  table: readonly CompteAlias[] = comptesAlias(),
): PersonnePrincipale | null {
  const cible = normaliser(objectId)
  if (!cible) return null
  return table.find((a) => normaliser(a.objectId) === cible)?.principal ?? null
}

/** Le compte partagé de cet Object ID, ou null. Identifiant vide : jamais. */
export function comptePartage(
  objectId: string | undefined,
  partages: readonly ComptePartage[] = comptesPartages(),
): ComptePartage | null {
  const cible = normaliser(objectId)
  if (!cible) return null
  return partages.find((p) => normaliser(p.objectId) === cible) ?? null
}

/**
 * Nom d'une personne de la table (alias ou appareil partagé), d'après son UPN.
 * Pour la présence : une ligne de SCE_UserPrefs créée DEPUIS la tablette a la
 * tablette pour « Créé par », pas la personne qu'elle représente.
 */
export function nomDansLaTable(
  upn: string | undefined,
  table: readonly CompteAlias[] = comptesAlias(),
  partages: readonly ComptePartage[] = comptesPartages(),
): string | null {
  const cible = normaliser(upn)
  if (!cible) return null
  const personnes = [...table.map((a) => a.principal), ...partages.flatMap((p) => p.personnes)]
  return personnes.find((p) => normaliser(p.upn) === cible)?.nom ?? null
}

/** `x_gmail.com#EXT#@tenant.onmicrosoft.com` → `x@gmail.com` ; le reste en minuscules. */
export function adresseNormalisee(adresse: string | undefined): string {
  const a = normaliser(adresse)
  const ext = a.indexOf('#ext#')
  if (ext < 0) return a
  const local = a.slice(0, ext)
  const i = local.lastIndexOf('_')
  return i < 0 ? local : `${local.slice(0, i)}@${local.slice(i + 1)}`
}

/** Repli de relecture de l'auteur quand Graph ne donne pas l'Object ID. */
function principalDeLAdresse(
  adresse: string | undefined,
  table: readonly CompteAlias[],
): PersonnePrincipale | null {
  const a = adresseNormalisee(adresse)
  if (!a) return null
  const empreinte = sha256Hex(a)
  return table.find((c) => c.empreinteAdresse && normaliser(c.empreinteAdresse) === empreinte)
    ?.principal ?? null
}

function partageDeLAdresse(
  adresse: string | undefined,
  partages: readonly ComptePartage[],
): ComptePartage | null {
  const a = adresseNormalisee(adresse)
  if (!a) return null
  const empreinte = sha256Hex(a)
  return partages.find((p) => p.empreinteAdresse && normaliser(p.empreinteAdresse) === empreinte) ?? null
}

/** L'auteur d'un élément tel que Graph le renvoie (`createdBy.user`). */
export interface AuteurGraph {
  id?: string
  email?: string
  displayName?: string
}

/**
 * L'auteur d'un élément SharePoint, ramené à la personne qu'il représente.
 *
 * Appelé par les mappers (problèmes, activité), À LA SOURCE : tout ce qui lit
 * le déclarant en aval — Mes déclarations, statistiques, trigramme de l'export
 * « Montage et Tests » — voit la personne, sans avoir à connaître cette table.
 */
export function auteurCanonique(
  auteur: AuteurGraph | undefined,
  table: readonly CompteAlias[] = comptesAlias(),
  partages: readonly ComptePartage[] = comptesPartages(),
  /** Colonne SaisiPar de la ligne : qui tenait l'appareil partagé. */
  saisiPar?: string,
): AuteurGraph | undefined {
  const partage = comptePartage(auteur?.id, partages) ?? partageDeLAdresse(auteur?.email, partages)
  if (partage) {
    // Seule une personne DE CET APPAREIL est crue : SaisiPar est une colonne
    // texte, modifiable à la main dans SharePoint.
    const cible = normaliser(saisiPar)
    const personne = cible ? partage.personnes.find((p) => normaliser(p.upn) === cible) : undefined
    if (personne) return { id: auteur?.id, email: personne.email, displayName: personne.nom }
    // Sans elle, on sait d'où, pas qui.
    return { ...auteur, displayName: partage.libelle }
  }

  const principal =
    principalDuCompte(auteur?.id, table) ?? principalDeLAdresse(auteur?.email, table)
  if (!principal) return auteur
  return { id: auteur?.id, email: principal.email, displayName: principal.nom }
}
